import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import {
  ACCESS_URL, dayUrl, searchRequest, searchProblem, columns, dayRange, wallMinutes, wallTimeLabel,
  showStatusBanner, bannerText, markerView, errorText, lienView, PX_PER_MIN, STATUS_STYLES, LEGEND,
  LEGEND_SHORT, statusStyle, isShort, timeShort, shortType, fitName, namePx, makeMeasure, blockStyle, flags, visitLabel,
  TODOS, todoCounts, dimmed, keyAction, laneBox, visibleVisits,
  HOLD_TODO, holdsIncluded, holdCount, holdDimmed, showHoldsNote,
} from "../lib/clinicSchedule";
import {
  CalendarDays, Loader2, RefreshCw, Search, AlertTriangle, X, Signature, FileWarning, FileCheck, UserX,
} from "lucide-react";

// P7 (clinic-schedule-page), part A: VIEW ONLY. A clinic's day from Raintree and a Raintree
// patient search. Book / move / cancel arrive in part B, each behind its own setting.
// Patient numbers, names and birth dates are sent in POST bodies only (lib/clinicSchedule.js).
// Rebuild 4: status is the block's colour, flags are icons with tooltips, every block has a
// screen-reader label in words. One element per visit, placed from start, end and column (P38's
// drag attaches here later).

const ICONS = { Signature, FileWarning, FileCheck, UserX };
const TIME_COL_PX = 56;
const HEADER_PX = 44;

function FlagIcons({ list }) {
  return (
    <span className="flex items-center gap-0.5 flex-shrink-0">
      {list.map((f) => {
        const I = ICONS[f.icon];
        return (
          <span key={f.key} title={f.words} data-flag={f.key} className={f.tone}>
            <I size={12} aria-hidden="true" />
          </span>
        );
      })}
    </span>
  );
}

// One element per visit: a native button (Tab focuses it, Enter or Space opens it).
function VisitBlock({ v, lo, colWidth, providerName, filter, onOpen, measure }) {
  const s = wallMinutes(v.start);
  if (s === null) return null;
  const st = statusStyle(v);
  const short = isShort(v);
  const fl = flags(v);
  const box = laneBox(v, colWidth);
  const top = (s - lo) * PX_PER_MIN;
  const height = Math.max(minutesHeight(v), 20);
  const name = fitName(v.name, namePx(box.width, fl.length, short), measure);
  return (
    <button
      type="button"
      data-testid="cs-visit"
      data-visit-id={v.id}
      data-provider={v.provider}
      data-start={v.start}
      data-end={v.end}
      data-status={st.key}
      aria-label={visitLabel(v, providerName)}
      onClick={() => onOpen(v)}
      className={`absolute z-10 rounded text-left overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500 transition-opacity ${dimmed(v, filter) ? "opacity-25" : ""}`}
      style={{ top, height, left: box.left, width: box.width, padding: "4px 8px", ...blockStyle(st.key) }}
    >
      {short ? (
        <div className="flex items-center gap-1 min-w-0">
          <span className={`text-[13px] leading-4 font-medium text-slate-900 whitespace-nowrap overflow-hidden ${st.struck ? "line-through" : ""}`}>{name}</span>
          <span className="ml-auto"><FlagIcons list={fl} /></span>
        </div>
      ) : (
        <>
          <div className={`text-[13px] leading-4 font-medium text-slate-900 whitespace-nowrap overflow-hidden ${st.struck ? "line-through" : ""}`}>{name}</div>
          <div className="flex items-center gap-1 min-w-0 text-[12px] leading-4 text-slate-600">
            <span className="truncate">{timeShort(v.start)} {shortType(v.type, v.type_label)}</span>
            <span className="ml-auto"><FlagIcons list={fl} /></span>
          </div>
        </>
      )}
    </button>
  );
}

function minutesHeight(v) {
  const s = wallMinutes(v.start);
  const e = wallMinutes(v.end);
  return ((e !== null && s !== null && e > s ? e : (s ?? 0) + 15) - (s ?? 0)) * PX_PER_MIN;
}

function BlockBand({ b, lo }) {
  const s = wallMinutes(b.start);
  const e = wallMinutes(b.end);
  if (s === null) return null;
  return (
    <div
      data-testid="cs-block"
      className="absolute left-0 right-0 z-0 bg-slate-100 text-[11px] text-slate-500 px-2 pt-0.5"
      style={{ top: (s - lo) * PX_PER_MIN, height: Math.max(((e ?? s + 15) - s) * PX_PER_MIN, 12) }}
    >
      {b.label}
    </div>
  );
}

// An open RVNP / RVRA hold (N6 v1.4): a dashed purple outline, no fill, behind the visits. View only:
// not a button, never bookable from the band.
const HOLD_COLOUR = "#7c3aed";   // violet-600
function HoldBand({ h, lo, filter }) {
  const s = wallMinutes(h.start);
  const e = wallMinutes(h.end);
  if (s === null || e === null) return null;
  return (
    <div
      data-testid="cs-hold"
      data-hold-category={h.category}
      data-provider={h.provider}
      data-start={h.start}
      data-end={h.end}
      className={`absolute z-0 rounded text-[11px] leading-4 px-2 pt-1 transition-opacity ${holdDimmed(filter) ? "opacity-25" : ""}`}
      style={{ top: (s - lo) * PX_PER_MIN, height: (e - s) * PX_PER_MIN, left: 2, right: 2,
        border: `1px dashed ${HOLD_COLOUR}`, background: "transparent", color: HOLD_COLOUR }}
      title={`${h.label}, ${wallTimeLabel(h.start)} to ${wallTimeLabel(h.end)} (open, view only)`}
    >
      {h.label}
    </div>
  );
}

function Legend({ showCancelled, holds }) {
  const keys = showCancelled ? [...LEGEND, "cancelled"] : LEGEND;
  return (
    <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-600" data-testid="cs-legend">
      {keys.map((k) => (
        <span key={k} className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm" style={blockStyle(k)} />
          <span className={STATUS_STYLES[k].struck ? "line-through" : ""}>{LEGEND_SHORT[k] || STATUS_STYLES[k].words}</span>
        </span>
      ))}
      <span className="inline-flex items-center gap-1"><FileCheck size={12} className="text-emerald-600" aria-hidden="true" /> Lien signed</span>
      {holds && (
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm" style={{ border: `1px dashed ${HOLD_COLOUR}` }} /> Open hold
        </span>
      )}
    </div>
  );
}

function TodoStrip({ counts, filter, setFilter, holds }) {
  const items = holds === null ? TODOS : [...TODOS, HOLD_TODO];
  const all = holds === null ? counts : { ...counts, [HOLD_TODO.key]: holds };
  return (
    <div className="flex flex-wrap gap-2" data-testid="cs-todo">
      {items.map((t) => (
        <button key={t.key} type="button" aria-pressed={filter === t.key}
          onClick={() => setFilter(filter === t.key ? null : t.key)}
          className={`rounded-md border px-3 py-1 text-sm ${filter === t.key ? "border-blue-500 bg-blue-50 text-blue-800" : "border-slate-300 bg-white text-slate-700"}`}>
          {t.label} <span className="font-semibold">{all[t.key] || 0}</span>
        </button>
      ))}
    </div>
  );
}

function DayGrid({ day, showCancelled, filter, onOpen }) {
  // Names are fitted to their rendered width, measured in the block's real font.
  const measure = useMemo(() => makeMeasure(typeof document !== "undefined" && document.body
    ? window.getComputedStyle(document.body).fontFamily : ""), []);
  const { lo, hi } = dayRange(day);
  const cols = columns(day, showCancelled);
  const hours = [];
  for (let m = lo; m <= hi; m += 60) hours.push(m);
  const height = (hi - lo) * PX_PER_MIN;
  if (!cols.length) return <p className="text-sm text-slate-500 py-6">No therapists or visits on this day.</p>;
  const hourLabel = (m) => wallTimeLabel(`T${String(Math.floor(m / 60)).padStart(2, "0")}:00`).replace(":00", "");
  return (
    <div className="overflow-auto border border-slate-200 rounded-lg bg-white" style={{ maxHeight: "70vh" }} data-testid="cs-grid">
      <div className="relative" style={{ width: TIME_COL_PX + cols.reduce((n, c) => n + c.width, 0) }}>
        <div className="flex sticky top-0 z-30 bg-white border-b border-slate-200" style={{ height: HEADER_PX }}>
          <div className="sticky left-0 z-40 bg-white border-r border-slate-200" style={{ width: TIME_COL_PX }} />
          {cols.map((c) => (
            <div key={c.code || "none"} className="px-2 py-1 border-r border-slate-100 flex-shrink-0" style={{ width: c.width }}>
              <div className="text-xs font-semibold text-slate-800 truncate" title={c.name}>{c.name}</div>
              <div className="text-[11px] text-slate-500">{c.visits.length} visit{c.visits.length === 1 ? "" : "s"}
                {c.holds.length > 0 && `, ${c.holds.length} hold${c.holds.length === 1 ? "" : "s"}`}</div>
            </div>
          ))}
        </div>
        <div className="flex">
          <div className="sticky left-0 z-20 bg-white border-r border-slate-200 flex-shrink-0 relative" style={{ width: TIME_COL_PX, height }}>
            {hours.map((m) => (
              <div key={m} className="absolute right-1.5 text-[11px] text-slate-400" style={{ top: (m - lo) * PX_PER_MIN + 2 }}>{hourLabel(m)}</div>
            ))}
          </div>
          {cols.map((c) => (
            <div key={c.code || "none"} className="relative border-r border-slate-100 flex-shrink-0" style={{ width: c.width, height }}>
              {hours.map((m) => (
                <div key={m} className="absolute left-0 right-0 border-t border-slate-200" style={{ top: (m - lo) * PX_PER_MIN }} />
              ))}
              {c.blocks.map((b) => <BlockBand key={`b-${b.id}-${b.start}`} b={b} lo={lo} />)}
              {c.holds.map((h) => <HoldBand key={`h-${h.category}-${h.start}`} h={h} lo={lo} filter={filter} />)}
              {c.visits.map((v) => (
                <VisitBlock key={`v-${v.id}-${v.start}`} v={v} lo={lo} colWidth={c.width} providerName={c.name}
                  filter={filter} onOpen={onOpen} measure={measure} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// The details panel, at the right of the grid. Esc or X closes it.
function VisitPanel({ v, providerName, onClose }) {
  if (!v) return null;
  const st = statusStyle(v);
  const m = markerView(v.marker);
  const l = lienView(v.lien);
  return (
    <aside className="w-72 flex-shrink-0 border border-slate-200 rounded-lg bg-white p-3 text-sm space-y-1 self-start" data-testid="cs-visit-panel" aria-label="Visit details">
      <div className="flex items-start gap-2">
        <span className={`font-semibold text-slate-900 ${st.struck ? "line-through" : ""}`}>{v.name || "—"}</span>
        <button type="button" onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-700" aria-label="Close">
          <X size={16} />
        </button>
      </div>
      <div className="text-slate-700">{wallTimeLabel(v.start)}–{wallTimeLabel(v.end)}</div>
      <div className="text-slate-700">{v.type_label || v.type || ""} · {providerName}</div>
      <div className="text-slate-700">Raintree status: {v.status_label}</div>
      {v.unconfirmed && <div className="text-amber-700">Raintree couldn't confirm this visit's status. Refresh to try again.</div>}
      <div className="text-slate-700">{v.has_card ? "Has a WestStar card." : "No WestStar card."}</div>
      {m && <div className="text-slate-700">{m.title || m.text}</div>}
      {l && <div className="text-emerald-700">{l.title}</div>}
      {/* P7 part B: visit actions render here */}
    </aside>
  );
}

function PatientSearchPanel({ location }) {
  const [form, setForm] = useState({ first: "", last: "", dob: "", pn: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const run = async (q) => {
    const problem = searchProblem(q);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { url, body } = searchRequest({ ...q, location });
      const res = await api.post(url, body);
      setResult(res.data);
    } catch (err) {
      setError(errorText(err, "Couldn't search Raintree. Try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white p-3 space-y-3" data-testid="cs-search">
      <div className="flex flex-wrap gap-2 items-end">
        <label className="text-xs text-slate-600">First<input className="block border rounded px-2 py-1 text-sm" value={form.first} onChange={set("first")} /></label>
        <label className="text-xs text-slate-600">Last<input className="block border rounded px-2 py-1 text-sm" value={form.last} onChange={set("last")} /></label>
        <label className="text-xs text-slate-600">Birth date<input className="block border rounded px-2 py-1 text-sm" placeholder="MM/DD/YYYY" value={form.dob} onChange={set("dob")} /></label>
        <span className="text-xs text-slate-400 pb-1.5">or</span>
        <label className="text-xs text-slate-600">Patient #<input className="block border rounded px-2 py-1 text-sm w-28" value={form.pn} onChange={set("pn")} /></label>
        <button type="button" onClick={() => run(form)} disabled={busy} data-testid="cs-search-btn"
          className="inline-flex items-center gap-1 rounded bg-blue-600 text-white text-sm px-3 py-1.5 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Search Raintree
        </button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {result && (
        <div className="space-y-2">
          {result.message && <p className="text-sm text-amber-700">{result.message}</p>}
          {result.candidates.length === 0 && !result.message && <p className="text-sm text-slate-500">No patients found.</p>}
          {result.candidates.map((c) => (
            <div key={c.pn} className="border border-slate-100 rounded p-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{c.first_name} {c.last_name}</span>
                <span className="text-slate-500">{c.birth_date}</span>
                <span className="text-slate-500 font-mono">#{c.pn}</span>
                {c.has_card && <span className="text-[10px] rounded bg-blue-100 text-blue-700 px-1">WestStar card</span>}
                {c.cases === null && (
                  <button type="button" className="ml-auto text-xs text-blue-600 underline" onClick={() => run({ pn: c.pn })}>
                    Show cases
                  </button>
                )}
              </div>
              {Array.isArray(c.cases) && (
                <div className="mt-1 space-y-0.5">
                  {c.no_active_case && <p className="text-xs text-amber-700">{result.no_active_case_text}</p>}
                  {c.cases.map((k) => (
                    <div key={k.case_num} className={`text-xs ${k.active ? "text-slate-700" : "text-slate-400"}`}>
                      Case {k.case_num} · {k.discipline} · {k.location} · {k.status}{k.active ? "" : " (inactive)"}
                      {/* P7 part B: book from a case renders here */}
                    </div>
                  ))}
                </div>
              )}
              {c.cases_error && <p className="text-xs text-amber-700">Couldn't load this patient's cases. Try again.</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ClinicSchedulePage() {
  const [clinics, setClinics] = useState(null);
  const [location, setLocation] = useState("");
  const [date, setDate] = useState("");
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showCancelled, setShowCancelled] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState(null);
  const seq = useRef(0);

  useEffect(() => {
    api.get(ACCESS_URL)
      .then((res) => {
        const list = res.data?.clinics || [];
        setClinics(list);
        if (list.length) {
          setLocation(list[0].code);
          setDate(list[0].today);
        }
      })
      .catch((err) => {
        setClinics([]);
        setError(errorText(err, "The clinic schedule isn't on for your login."));
      });
  }, []);

  // Two-step load: "none" draws the grid fast (one Raintree call), then "all" fills the statuses.
  const load = useCallback(async () => {
    const first = dayUrl(location, date, "none");
    if (!first) return;
    const mine = ++seq.current;
    setLoading(true);
    setError("");
    setSelected(null);
    try {
      const quick = await api.get(first);
      if (mine !== seq.current) return;
      setDay(quick.data);
      const full = await api.get(dayUrl(location, date, "all"));
      if (mine !== seq.current) return;
      setDay(full.data);
    } catch (err) {
      if (mine === seq.current) setError(errorText(err, "Couldn't load the schedule from Raintree. Try Refresh."));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [location, date]);

  useEffect(() => {
    load();
  }, [load]);

  // Esc closes the details panel.
  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => {
      if (keyAction(e.key) === "close") setSelected(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selected]);

  const counts = useMemo(() => todoCounts(visibleVisits(day?.visits, showCancelled)), [day, showCancelled]);

  const pick = (code) => {
    setLocation(code);
    const c = (clinics || []).find((x) => x.code === code);
    if (c && !date) setDate(c.today);
  };

  const panelVisit = selected
    ? (day?.visits || []).find((x) => x.id === selected.id && x.start === selected.start) || selected
    : null;

  return (
    <div className="p-6 space-y-3" data-testid="clinic-schedule-page">
      <div className="flex items-center gap-2">
        <CalendarDays size={20} className="text-blue-600" />
        <h1 className="text-xl font-bold text-slate-900">Clinic schedule</h1>
      </div>

      {clinics && clinics.length > 0 && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-slate-600">Clinic
            <select className="block border rounded px-2 py-1 text-sm" value={location} onChange={(e) => pick(e.target.value)} data-testid="cs-clinic">
              {clinics.map((c) => <option key={c.code} value={c.code}>{c.name}{c.city ? ` (${c.city})` : ""}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-600">Day
            <input type="date" className="block border rounded px-2 py-1 text-sm" value={date} onChange={(e) => setDate(e.target.value)} data-testid="cs-date" />
          </label>
          <button type="button" onClick={load} disabled={loading} data-testid="cs-refresh"
            className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white text-sm px-3 py-1.5 disabled:opacity-50">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <label className="text-xs text-slate-600 inline-flex items-center gap-1 pb-1.5 ml-auto">
            <input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} data-testid="cs-show-cancelled" />
            Show cancelled
          </label>
          {loading && <span className="inline-flex items-center gap-1 text-xs text-slate-500 pb-1.5"><Loader2 size={12} className="animate-spin" /> Loading from Raintree…</span>}
        </div>
      )}

      {error && <p className="text-sm text-red-600" data-testid="cs-error">{error}</p>}

      {showStatusBanner(day) && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="cs-status-banner">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <span>{bannerText(day)}</span>
        </div>
      )}

      {day && (
        <>
          <TodoStrip counts={counts} filter={filter} setFilter={setFilter}
            holds={holdsIncluded(day) ? holdCount(day) : null} />
          <Legend showCancelled={showCancelled} holds={holdsIncluded(day)} />
          {showHoldsNote(day) && (
            <p className="text-xs text-amber-700" data-testid="cs-holds-note">Couldn't load some holds</p>
          )}
          <div className="flex gap-3 items-start">
            <div className="flex-1 min-w-0">
              <DayGrid day={day} showCancelled={showCancelled} filter={filter} onOpen={setSelected} />
            </div>
            {panelVisit && (
              <VisitPanel
                v={panelVisit}
                providerName={(day?.providers || []).find((p) => p.code === panelVisit.provider)?.name || panelVisit.provider || ""}
                onClose={() => setSelected(null)}
              />
            )}
          </div>
        </>
      )}

      {clinics && clinics.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-700">Find a patient in Raintree</h2>
          <PatientSearchPanel location={location} />
        </div>
      )}
      {/* P7 part B: the booking panel renders here */}
    </div>
  );
}
